class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // For meaningful search terms, run both predictive search AND full search in parallel
    if (searchTerm.length >= 3) {
      Promise.all([
        this.getPredictiveSearchResults(searchTerm),
        this.getFullSearchResults(searchTerm)
      ])
      .then(([predictiveResults, fullSearchResults]) => {
        const combinedResults = this.combineAllResults(predictiveResults, fullSearchResults, searchTerm);
        this.finalizeResults(combinedResults, queryKey);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
      });
    } else {
      // For short search terms, just use predictive search
      this.getPredictiveSearchResults(searchTerm)
        .then((results) => {
          this.finalizeResults(results, queryKey);
        })
        .catch((error) => {
          if (error?.code === 20) {
            return;
          }
          this.close();
          throw error;
        });
    }
  }

  async getPredictiveSearchResults(searchTerm) {
    const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    });
    
    if (!response.ok) {
      throw new Error(`Predictive search failed: ${response.status}`);
    }
    
    const text = await response.text();
    return new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('#shopify-section-predictive-search').innerHTML;
  }

  async getFullSearchResults(searchTerm) {
    try {
      // Use the full search API which DOES search descriptions
      const response = await fetch(`${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&limit=12&view=json`, {
        signal: this.abortController.signal,
      });
      
      if (!response.ok) {
        return null;
      }
      
      const html = await response.text();
      const searchPageDoc = new DOMParser().parseFromString(html, 'text/html');
      const productElements = searchPageDoc.querySelectorAll('.ed-product-block, .card-wrapper, [data-product-id]');
      
      return productElements;
    } catch (error) {
      return null;
    }
  }

  countProductResults(resultsMarkup) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    const productItems = tempDiv.querySelectorAll('#predictive-search-results-products-list li');
    return productItems.length;
  }

  getSupplementarySearchResults(searchTerm, originalResults, queryKey) {
    // Create a more targeted search to find products that match in descriptions
    const searchUrl = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&limit=8`;
    
    fetch(searchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          // Fallback to original results if search fails
          this.finalizeResults(originalResults, queryKey);
          return;
        }
        return response.text();
      })
      .then((html) => {
        if (html) {
          // Parse the search results page to extract additional products
          const searchPageDoc = new DOMParser().parseFromString(html, 'text/html');
          const productElements = searchPageDoc.querySelectorAll('.ed-product-block, .card-wrapper, [data-product-id]');
          
          if (productElements.length > 0) {
            // Combine the results
            const combinedResults = this.combineSearchResults(originalResults, productElements, searchTerm);
            this.finalizeResults(combinedResults, queryKey);
          } else {
            this.finalizeResults(originalResults, queryKey);
          }
        } else {
          this.finalizeResults(originalResults, queryKey);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        // Fallback to original results if search fails
        this.finalizeResults(originalResults, queryKey);
      });
  }

  finalizeResults(results, queryKey) {
    // Save bandwidth keeping the cache in all instances synced
    this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] = results;
    });
    this.renderSearchResults(results);
  }

  countProductResults(htmlString) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const productItems = tempDiv.querySelectorAll('#predictive-search-results-products-list li');
    return productItems.length;
  }

  combineAllResults(predictiveResults, fullSearchElements, searchTerm) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = predictiveResults;
    
    // Count existing predictive search products
    const existingProductCount = this.countProductResults(predictiveResults);
    console.log(`Predictive search found ${existingProductCount} products`);
    
    // If we have full search results and few predictive results, enhance with full search
    if (fullSearchElements && fullSearchElements.length > 0 && existingProductCount < 5) {
      
      // Get existing product URLs to avoid duplicates
      const existingProductUrls = new Set();
      const existingProducts = tempDiv.querySelectorAll('#predictive-search-results-products-list li a');
      existingProducts.forEach(link => {
        if (link.href) {
          const cleanUrl = link.href.split('?')[0];
          existingProductUrls.add(cleanUrl);
        }
      });

      // Find or create the products list container
      let productsList = tempDiv.querySelector('#predictive-search-results-products-list');
      
      if (!productsList) {
        // If no products section exists, create one
        const resultsContainer = tempDiv.querySelector('#predictive-search-results');
        if (resultsContainer) {
          const productsSection = document.createElement('div');
          productsSection.className = 'predictive-search__result-group';
          productsSection.innerHTML = `
            <div>
              <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing p-0">
                Products
              </h2>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled flex flex-wrap" role="group" aria-labelledby="predictive-search-products">
              </ul>
            </div>
          `;
          resultsContainer.appendChild(productsSection);
          productsList = productsSection.querySelector('#predictive-search-results-products-list');
        }
      }
      
      if (productsList) {
        // Add new products (limit based on existing count)
        let addedCount = 0;
        const maxTotal = 8;
        const maxAdditional = Math.max(1, maxTotal - existingProductCount);
        
        for (const productElement of fullSearchElements) {
          if (addedCount >= maxAdditional) break;
          
          const productInfo = this.extractProductInfo(productElement);
          if (productInfo && !existingProductUrls.has(productInfo.url.split('?')[0])) {
            const listItem = this.createPredictiveSearchItem(productInfo, existingProductCount + addedCount);
            productsList.appendChild(listItem);
            addedCount++;
            existingProductUrls.add(productInfo.url.split('?')[0]);
          }
        }
      }
    }
    
    return tempDiv.innerHTML;
  }

  extractProductInfo(productElement) {
    let titleElement, imageElement, secondaryImageElement, linkElement, priceElement, labelsElement, optionsElement;
    
    if (productElement.classList.contains('ed-product-block')) {
      // Extract from ed-product-block structure
      titleElement = productElement.querySelector('.product-title');
      imageElement = productElement.querySelector('.product-image img:not(.secondary-image)');
      secondaryImageElement = productElement.querySelector('.product-image .secondary-image');
      linkElement = productElement.querySelector('a[href*="/products/"]');
      priceElement = productElement.querySelector('.price-block');
      labelsElement = productElement.querySelector('.labels');
      optionsElement = productElement.querySelector('.options');
    } else {
      // Extract from card-product or other structures
      titleElement = productElement.querySelector('.card__heading a, .product-title, h2, h3, .h1, .h2, .h3');
      imageElement = productElement.querySelector('img');
      linkElement = productElement.querySelector('a[href*="/products/"]') || productElement.closest('a[href*="/products/"]');
      priceElement = productElement.querySelector('.price, .money, .price-block, .card-information .price');
      
      // Try to find labels and options in alternative structures
      labelsElement = productElement.querySelector('.card__badge, .badge, .labels');
      optionsElement = productElement.querySelector('.product-form__buttons, .variant-picker, .options');
    }
    
    if (titleElement && linkElement) {
      return {
        title: titleElement.textContent.trim(),
        url: linkElement.href,
        image: imageElement ? (imageElement.src || imageElement.dataset.src || '') : '',
        secondaryImage: secondaryImageElement ? (secondaryImageElement.src || secondaryImageElement.dataset.src || '') : '',
        imageAlt: imageElement ? imageElement.alt : titleElement.textContent.trim(),
        price: priceElement ? priceElement.innerHTML.trim() : '',
        labels: labelsElement ? labelsElement.innerHTML.trim() : '',
        options: optionsElement ? optionsElement.innerHTML.trim() : ''
      };
    }
    
    return null;
  }

  createPredictiveSearchItem(productInfo, index) {
    const listItem = document.createElement('li');
    listItem.className = 'predictive-search__list-item';
    listItem.setAttribute('role', 'option');
    listItem.setAttribute('aria-selected', 'false');
    listItem.id = `predictive-search-option-product-enhanced-${index + 1}`;
    
    // Create the product item matching the exact ed-product-block structure
    listItem.innerHTML = `
      <div class="ed-product-block position-relative">
        <a href="${productInfo.url}" class="position-absolute"></a>
        <div class="product-image overflow-hidden">
          ${productInfo.image ? `<img src="${productInfo.image}" alt="${productInfo.imageAlt}" width="500" height="800" class="w-100" loading="lazy">` : ''}
          ${productInfo.secondaryImage ? `<img src="${productInfo.secondaryImage}" alt="${productInfo.imageAlt}" width="500" height="800" class="w-100 secondary-image" loading="lazy">` : ''}
          ${productInfo.labels ? `<div class="labels flex flex-column">${productInfo.labels}</div>` : ''}
          ${productInfo.options ? `<div class="options">${productInfo.options}</div>` : ''}
        </div>
        <div class="product-info flex flex-column">
          <a href="${productInfo.url}" class="wishlist-link"></a>
          <span class="product-title">${productInfo.title}</span>
          <div class="price-block flex">
            ${productInfo.price}
          </div>
        </div>
      </div>
    `;
    
    return listItem;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    document.querySelector('main').classList.add('searching');
    document.querySelector('.ed-header').classList.add('searching');
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    document.querySelector('main').classList.remove('searching');
    document.querySelector('.ed-header').classList.remove('searching');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
